import { useRef, useState } from "react";
import { TokenResult } from "@stripe/stripe-js";
import { useSnackbar } from "@powerdiary/components-library/lib";
import { CreateSCAModel, CreditCardInfo, SetupSCAModel, StripePaymentSource } from "../../../types/generated/models";
import { useCreateSCAIntent } from "../../Shared/StripePayment/useCreateSCAIntent";
import { useCreateOrUpdateCustomerCreditCard } from "../../Shared/StripePayment/useCreateOrUpdateCustomerCreditCard";
import { useRevokeCardConsent } from "../../Shared/StripePayment/useRevokeCardConsent";
import { PaymentFormType } from "./InvoiceStripePaymentShared/InvoiceStripePaymentShared.helpers";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { useStripePushPayment } from "./useStripePushPayment";

interface IAppointmentBookingMetadata {
    locationId: number;
    appointmentDate: string;
    appointmentOffset?: number;
    durationMinutes: number;
    appointmentType: string;
    notes?: string;
    // The selected booking client (related contact or shared-profile member). Falls through to
    // CreateSCAModel.clientId so the webhook books the appointment for this client rather than
    // defaulting to the authenticated portal user.
    bookingClientId?: number;
    // Required for class bookings — identifies the existing group appointment the client is
    // joining. Without it the webhook fails with ClassAppointmentMustBeProvided after the card
    // has already been charged.
    appointmentId?: number;
}

interface IUseStripePaymentProps {
    practitionerId: number;
    invoiceIds?: number[];
    serviceId?: number;
    editCardMode: boolean;
    creditCardInfo: CreditCardInfo;
    clientId?: number;
    stripePaymentSource: StripePaymentSource;
    appointmentBooking?: IAppointmentBookingMetadata;
    handleSuccessfulPayment: (
        paymentIntentId: string,
        hasTotalAmount: boolean,
        idempotencyKey: string
    ) => Promise<void>;
}

export function useStripePayment({
    practitionerId,
    invoiceIds,
    serviceId,
    editCardMode,
    clientId,
    creditCardInfo,
    stripePaymentSource,
    appointmentBooking,
    handleSuccessfulPayment,
}: IUseStripePaymentProps) {
    const [savingPayment, setSavingPayment] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { createSCAIntent } = useCreateSCAIntent(clientId);
    const { createOrUpdateCustomerCreditCard } = useCreateOrUpdateCustomerCreditCard();
    const { revokeCardConsent } = useRevokeCardConsent(clientId);
    const stripe = useStripe();
    const elements = useElements();
    const { track } = useAnalytics();
    const { waitForPaymentWebhook } = useStripePushPayment();

    // Stable idempotency key per "payment attempt context" (invoice/amount/card).
    // The same key is reused across retries so Stripe/CreatePayment treats them as
    // the same logical attempt. It is rotated only after a confirmed successful
    // CreatePayment, or when the amount/card context changes.
    const idempotencyKeyRef = useRef<string | null>(null);
    const idempotencyContextRef = useRef<string | null>(null);

    const buildIdempotencyContext = (amount: number, isUsingExistingCard: boolean, newCardIdentity?: string) => {
        const cardIdentity = isUsingExistingCard
            ? `saved:${creditCardInfo?.cardNumber ?? ""}:${creditCardInfo?.expMonth ?? ""}:${creditCardInfo?.expYear ?? ""}`
            : `new:${newCardIdentity ?? "unknown"}`;
        return `${invoiceIds?.join(",") ?? ""}|${serviceId ?? ""}|${amount}|${cardIdentity}`;
    };

    const getOrCreateIdempotencyKey = (amount: number, isUsingExistingCard: boolean, newCardIdentity?: string) => {
        const context = buildIdempotencyContext(amount, isUsingExistingCard, newCardIdentity);
        if (idempotencyKeyRef.current === null || idempotencyContextRef.current !== context) {
            idempotencyKeyRef.current = crypto.randomUUID();
            idempotencyContextRef.current = context;
        }
        return idempotencyKeyRef.current;
    };

    const resetIdempotencyKey = () => {
        idempotencyKeyRef.current = null;
        idempotencyContextRef.current = null;
    };

    const handlePayment = async (values: PaymentFormType, amount: number, hasTotalAmount: boolean) => {
        const cardElement = elements.getElement("cardNumber");
        setSavingPayment(true);

        const isUsingExistingCard = !editCardMode && !!creditCardInfo;

        const shouldCreateToken = !isUsingExistingCard;

        try {
            const tokenResult: TokenResult = shouldCreateToken
                ? await stripe.createToken(cardElement)
                : ({} as TokenResult);

            if (tokenResult.error) {
                enqueueSnackbar(tokenResult.error.message, { variant: "error", alertTitle: "Stripe error" });
                return;
            }

            const newCardIdentity = !isUsingExistingCard
                ? (tokenResult.token?.card?.fingerprint ??
                  `${tokenResult.token?.card?.last4 ?? ""}:${tokenResult.token?.card?.exp_month ?? ""}:${tokenResult.token?.card?.exp_year ?? ""}`)
                : undefined;

            const idempotencyKey = getOrCreateIdempotencyKey(amount, isUsingExistingCard, newCardIdentity);

            const invoiceNumber = invoiceIds?.length === 1 ? invoiceIds[0] : undefined;

            const createSCAIntentModel: CreateSCAModel = {
                amount: amount,
                saveDetails: values.storeCard,
                idempotencyKey: idempotencyKey,
                isOffline: isUsingExistingCard,
                practitionerId: practitionerId,
                invoiceNumber: invoiceNumber,
                invoiceIds: invoiceIds ?? [],
                serviceId: serviceId,
                stripePaymentSource: stripePaymentSource,
                ...(clientId && { clientId: clientId }),
                ...(appointmentBooking && {
                    locationId: appointmentBooking.locationId,
                    appointmentDate: appointmentBooking.appointmentDate,
                    appointmentOffset: appointmentBooking.appointmentOffset,
                    durationMinutes: appointmentBooking.durationMinutes,
                    appointmentType: appointmentBooking.appointmentType,
                    notes: appointmentBooking.notes,
                    hasTotalAmount: hasTotalAmount,
                    appointmentId: appointmentBooking.appointmentId,
                    ...(appointmentBooking.bookingClientId !== undefined && {
                        clientId: appointmentBooking.bookingClientId,
                    }),
                }),
            };

            const confirmPaymentResponse = await createSCAIntent(createSCAIntentModel);

            if (isUsingExistingCard) {
                if (confirmPaymentResponse.requiresAction) {
                    const { error: handleActionError } = await stripe.handleNextAction({
                        clientSecret: confirmPaymentResponse.clientSecret,
                    });
                    if (handleActionError) {
                        // Reset so the next attempt gets a fresh PaymentIntent — the current one
                        // is in requires_payment_method after failed 3DS and cannot be reused.
                        resetIdempotencyKey();
                        throw new Error(handleActionError.message);
                    }
                }

                if (!values.storeCard) {
                    await revokeCardConsent(practitionerId);
                }

                if (confirmPaymentResponse.pushSettings) {
                    const webhookResult = await waitForPaymentWebhook(
                        confirmPaymentResponse.pushSettings,
                        confirmPaymentResponse.paymentIntentId
                    );
                    if (webhookResult && !webhookResult.isSuccess) {
                        throw new Error(webhookResult.errorMessage || "Payment processing failed.");
                    }
                }

                await handleSuccessfulPayment(confirmPaymentResponse.paymentIntentId, hasTotalAmount, idempotencyKey);
                track("invoice_payment_success", {
                    invoice_count: invoiceIds?.length ?? 0,
                    amount: amount,
                    payment_method: editCardMode || !creditCardInfo ? "new_card" : "saved_card",
                });
                resetIdempotencyKey();
                return;
            }

            const confirmStripeCardPayment = await stripe.confirmCardPayment(confirmPaymentResponse.clientSecret, {
                payment_method: { card: cardElement },
            });

            if (confirmStripeCardPayment.error) {
                enqueueSnackbar(confirmStripeCardPayment.error.message, {
                    variant: "error",
                    alertTitle: "Stripe error",
                });
                return;
            }

            if (values.storeCard) {
                const createOrUpdateCustomerCreditCardModel: SetupSCAModel = {
                    paymentMethodId: confirmStripeCardPayment.paymentIntent.payment_method as string,
                    practitionerId: practitionerId,
                };
                await createOrUpdateCustomerCreditCard(createOrUpdateCustomerCreditCardModel);
            }

            if (confirmPaymentResponse.pushSettings) {
                const webhookResult = await waitForPaymentWebhook(
                    confirmPaymentResponse.pushSettings,
                    confirmPaymentResponse.paymentIntentId
                );
                if (webhookResult && !webhookResult.isSuccess) {
                    throw new Error(webhookResult.errorMessage || "Payment processing failed.");
                }
            }

            resetIdempotencyKey();
            await handleSuccessfulPayment(confirmPaymentResponse.paymentIntentId, hasTotalAmount, idempotencyKey);

            track("invoice_payment_success", {
                invoice_count: invoiceIds?.length ?? 0,
                amount: amount,
                payment_method: editCardMode || !creditCardInfo ? "new_card" : "saved_card",
            });
        } catch (error_) {
            const message = error_ instanceof Error ? error_.message : "An unexpected error occurred.";
            enqueueSnackbar(message, { variant: "error", alertTitle: "Payment error" });
        } finally {
            setSavingPayment(false);
        }
    };

    return {
        handlePayment,
        savingPayment,
    };
}
